import { Button, Card, Group, Textarea, Text, Flex } from '@mantine/core';
import { IconCheck, IconTrash, IconEdit } from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import { theme } from '../consilitheme';
import {
  useGetSourcesQuery,
  useCreateCategoryMutation,
} from '../endpoints/discovery';
import { useUserQuery } from '../endpoints/user';
import SourceSelector from './SourceSelector';
import CategorySelector from './CategorySelector';

const InterestItem = ({ item, onDelete, onEdit }) => {
  const [interest, setInterest] = useState(item);
  const [newTopic, setNewTopic] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customSource, setCustomSource] = useState('');
  const [isEditMode, setIsEditMode] = useState(item.edit);
  const [userId, setUserId] = useState(false);
  const [sourceOptions, setSourceOptions] = useState([]);
  const isComplete = interest?.subject && interest?.sources?.length;
  const hasSource = interest?.sources?.length;
  const hasTopics = interest?.categories?.length !== 0;

  const user = useUserQuery();
  const { data: sourcesData } = useGetSourcesQuery();
  const [createCategoryByName] = useCreateCategoryMutation();

  useEffect(() => {
    setUserId(user.data ? user.data[0].id : null);
  }, [user]);
  useEffect(() => {
    setSourceOptions(
      sourcesData?.filter(
        (source) =>
          !interest?.sources?.some(
            (interestSource) => source.id === interestSource.id
          )
      ) ?? []
    );
  }, [sourcesData]);
  const addTopic = async () => {
    if (newTopic) {
      try {
        const response = await createCategoryByName({
          name: newTopic,
          owner: userId,
        });
        setInterest({
          ...interest,
          categories: [...interest.categories, response.data],
        });
        setNewTopic('');
      } catch (error) {
        console.error('Error creating interest:', error);
      }
    }
  };

  const handleSubmit = (value) => {
    setSelectedSource(value);
    if (value === 'other') {
      setIsModalOpen(true);
    }
    if (value && value !== 'other' && !interest.sources.includes(value)) {
      setInterest({
        ...interest,
        sources: [
          ...interest.sources,
          sourceOptions.find((source) => source.name === value),
        ],
      });
      setSourceOptions(sourceOptions.filter((source) => source.name !== value));
      setSelectedSource('');
    }
  };

  const handleCustomSourceSubmit = () => {
    if (customSource && !interest.source.includes(customSource)) {
      setInterest({
        ...interest,
        sources: [...interest.sources, customSource],
      });
      setCustomSource('');
    }
    setIsModalOpen(false);
    setSelectedSource('');
  };

  const setTags = (newTags) => {
    setInterest({ ...interest, categories: newTags });
  };

  const setSources = (newsource) => {
    setInterest({ ...interest, sources: newsource });
  };

  const handleEdit = () => {
    onEdit(item.id, interest);
    setIsEditMode(false);
  };

  const handleEnableEdit = () => {
    setIsEditMode(true);
  };

  const removeTag = (tag, tagType) => {
    if (tagType === 'category') {
      setTags(
        interest.categories.filter((activeTag) => activeTag.name !== tag)
      );
    } else if (tagType === 'source') {
      setSources(
        interest.sources.filter((activeTag) => activeTag.name !== tag)
      );
      setSourceOptions([
        ...sourceOptions,
        interest.sources.filter((activeTag) => activeTag.name === tag)[0],
      ]);
    }
  };

  return (
    <Card
      shadow="md"
      radius="md"
      mt="10"
      withBorder
      pos="relative"
      key={`${interest.id}`}
      p="xl"
    >
      <Flex align="start">
        <SourceSelector
          isEditMode={isEditMode}
          hasSource={hasSource}
          selectedSource={selectedSource}
          sourcesData={sourceOptions}
          handleSubmit={(value) => handleSubmit(value)}
          isModalOpen={isModalOpen}
          interest={interest}
          setIsModalOpen={(value) => setIsModalOpen(value)}
          customSource={customSource}
          setCustomSource={(source) => setCustomSource(source)}
          handleCustomSourceSubmit={() => handleCustomSourceSubmit()}
          setSources={(newsource) => setSources(newsource)}
          removeTag={(tag, tagType) => removeTag(tag, tagType)}
        />
        {!isEditMode && (
          <Button
            onClick={handleEnableEdit}
            leftSection={<IconEdit size="14" />}
            size="xs"
            variant="default"
            ml="auto"
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <Group ml="auto">
            <Button
              onClick={() => onDelete(item.id)}
              style={{
                background: theme.colors.red[3],
                color: 'white',
              }}
              leftSection={<IconTrash size="14" />}
              size="xs"
              variant="light"
            >
              Delete
            </Button>
            {interest?.subject?.length && (
              <Button
                onClick={handleEdit}
                disabled={!hasSource || !isComplete}
                leftSection={<IconCheck size="14" />}
                size="xs"
                variant="default"
              >
                Save
              </Button>
            )}
          </Group>
        )}
      </Flex>
      <Text size="md" fw={600} mb={theme.padding.xs}>
        Research Topic
      </Text>
      {isEditMode ? (
        <Textarea
          disabled={!isEditMode || !hasSource}
          value={interest.subject ?? ''}
          onChange={(e) =>
            setInterest({ ...interest, subject: e.target.value })
          }
          placeholder="Describe the insight you’re after in as much detail as you require"
          autosize
          minRows={1}
          maxRows={5}
          size="md"
        />
      ) : (
        <>
          {!interest.subject ? (
            <Text size="md" c="gray">
              Describe the insight you’re after in as much detail as you require
            </Text>
          ) : (
            <Text size="md">{interest.subject}</Text>
          )}
        </>
      )}
      <CategorySelector
        isEditMode={isEditMode}
        hasSource={hasSource}
        newTopic={newTopic}
        setNewTopic={(topic) => setNewTopic(topic)}
        addTopic={() => addTopic()}
        hasTopics={hasTopics}
        isComplete={isComplete}
        handleEnableEdit={() => handleEnableEdit()}
        interest={interest}
        removeTag={(tag, tagType) => removeTag(tag, tagType)}
        setTags={(newTags) => setTags(newTags)}
      />
    </Card>
  );
};

export default InterestItem;
